import {combine, createEffect, createEvent, createStore, sample} from 'effector';

import {IS_DDA_MODE} from '@app/env';
import {showRestError} from '@app/errors';
import {MANAGEMENT_ROLES, TAXPAYER_ROLES} from '@entities/user/permissions';
import {AuthRequest, Profile, TokensData, UserRole} from '@entities/user/types';

import {authBroadcast} from '@shared/lib/auth/notification';
import {TokenStorage} from '@shared/lib/auth/tokens';

import * as api from './api';

export const isTaxpayerRole = (role: Profile['roles'][number] | null) =>
    Boolean(role && TAXPAYER_ROLES.includes(role));

export const isManagementRole = (role: Profile['roles'][number] | null) =>
    Boolean(role && MANAGEMENT_ROLES.includes(role));

const DDA_ADMIN_ROLE: UserRole[number] = 'DDA_ADMINISTRATOR';
const LOGIN_MODE_ERROR_MESSAGE = 'Access denied for current application';

const isProfileAllowedByBuildMode = (profile: Profile) => {
    const role = profile.roles?.[0] ?? null;
    return IS_DDA_MODE ? role === DDA_ADMIN_ROLE : role !== DDA_ADMIN_ROLE;
};

// --- API Implementation ---

// --- Effects ---
export const fetchProfileFx = createEffect(api.fetchProfile);
const loginFx = createEffect(api.authorize);
const logoutFx = createEffect(api.deauthorize);
const verifyOtpFx = createEffect(api.verifyMfa);
const sendOtpFx = createEffect(api.createMfa);

const cleanAuthDataFx = createEffect(() => TokenStorage.removeTokens());
const reloadPageFx = createEffect(() => window.location.reload());

// --- Events ---
const doLogin = createEvent<AuthRequest>();
export const doLogout = createEvent<boolean>(); // payload: redirectFromPrevPath
const resetAuthData = createEvent();
const authSyncReceived = createEvent<'LOGIN' | 'LOGOUT'>();
const loginRejectedByBuildMode = createEvent();

// --- Stores ---
const $profile = createStore<Profile | null>(null);
const $loginError = createStore<string | null>(null);
const $otpError = createStore<boolean>(false);
const $tempAuthData = createStore<TokensData | null>(null);
const $loginRedirectFromPrevPath = createStore<boolean>(true);

// --- Derived Stores (Selectors) ---
const $isAuth = combine(
    $profile,
    $tempAuthData,
    (profile, authData) => Boolean(profile) && !authData,
);
const $role = $profile.map((profile) => profile?.roles?.[0] ?? null);

// --- Logic & Relations (Internal) ---
$loginError
    .on(loginFx.failData, (_, {message}) => message)
    .on(loginRejectedByBuildMode, () => LOGIN_MODE_ERROR_MESSAGE)
    .reset(loginFx.done);
$otpError.on(verifyOtpFx.failData, () => true).reset([verifyOtpFx, sendOtpFx, doLogin]);

[loginFx.failData, logoutFx.failData, sendOtpFx.failData, verifyOtpFx.failData].forEach((fx) => {
    fx.watch(showRestError);
});
loginRejectedByBuildMode.watch(() => showRestError({message: LOGIN_MODE_ERROR_MESSAGE}));

sample({clock: doLogin, target: loginFx});

sample({clock: loginFx.doneData, target: fetchProfileFx});

const allowedProfileFetched = sample({
    clock: fetchProfileFx.done,
    filter: ({result}) => isProfileAllowedByBuildMode(result),
});

sample({
    clock: allowedProfileFetched,
    fn: ({params}) => params ?? null,
    target: $tempAuthData,
});

sample({
    clock: fetchProfileFx.doneData,
    filter: (profile) => !isProfileAllowedByBuildMode(profile),
    target: [loginRejectedByBuildMode, resetAuthData],
});

const otpSuccessWithData = sample({
    clock: verifyOtpFx.done,
    source: $tempAuthData,
    filter: (data) => Boolean(data),
});

otpSuccessWithData.watch((tokensData) => {
    if (!tokensData) return;
    TokenStorage.storeTokens(tokensData);
    authBroadcast.notifyLogin();
});

$tempAuthData.on(otpSuccessWithData, () => null).reset([resetAuthData, cleanAuthDataFx.finally]);

$loginRedirectFromPrevPath.on(doLogout, (_, payload) => payload);

sample({
    clock: doLogout,
    fn: () => TokenStorage.refreshToken,
    target: logoutFx,
});

sample({
    clock: [logoutFx.finally, resetAuthData],
    target: cleanAuthDataFx,
});

$profile.on(allowedProfileFetched, (_, {result}) => result).reset(cleanAuthDataFx.finally);

authBroadcast.subscribe((message) => {
    authSyncReceived(message.type);
});

sample({
    clock: authSyncReceived,
    filter: (type) => type === 'LOGIN',
    target: reloadPageFx,
});

const $userState = combine({
    profile: $profile,
    role: $role,
    name: $profile.map(
        (p) => `${p?.firstName ?? ''} ${p?.middleName ?? ''} ${p?.lastName ?? ''}`.trim() || '—',
    ),
    address: $profile.map(
        (p) =>
            `${p?.company.streetName ? p?.company.streetName + ',' : ''} ${
                p?.company.cityName ? p?.company.cityName + ',' : ''
            } ${p?.company.country ? p?.company.country + ',' : ''}`.trim() || '—',
    ),
    authData: $tempAuthData,
    isAuth: $isAuth,
    error: combine({
        login: $loginError,
        otp: $otpError,
    }),
    pending: combine(
        [loginFx.pending, fetchProfileFx.pending, verifyOtpFx.pending, sendOtpFx.pending],
        (list) => list.some(Boolean),
    ),
});

export const userModel = {
    state: $userState,
    role: $role,
    isDirector: $role.map((role) => role === 'DIRECTOR'),
    isEmployee: $role.map((role) => role === 'EMPLOYEE'),
    isOperator: $role.map((role) => role === 'OPERATOR'),
    isInspector: $role.map((role) => role === 'INSPECTOR'),
    isChiefInspector: $role.map((role) => role === 'CHIEF_INSPECTOR'),
    isAdminDDA: $role.map((role) => role === 'DDA_ADMINISTRATOR'),
    isTaxpayer: $role.map(isTaxpayerRole),
    isManagement: $role.map(isManagementRole),
    isAuth: $isAuth,
    login: doLogin,
    logout: doLogout,
    resetAuth: resetAuthData,
    sendOtp: sendOtpFx,
    verifyOtp: verifyOtpFx,
};
